import React, { useEffect } from "react";
import { Box, Container } from "@mui/material";
import { Facebook, Linkedin, Instagram, ArrowRight } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import Logo from "icons/new-logo.svg";

export default function Footer({ setFooterRendered, variant }) {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const currentCandidate = useSelector((state) => state.current_candidate);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const locale = i18nLocale || "fr";
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://squarehub.eu";
  const isRecruiter = variant === "recruiter";
  const isLoggedIn = !!currentCandidate || !!currentRecruiter;
  const spacePath = (page) => {
    if (currentCandidate) return `${baseUrl}/${locale}/candidate/${page}`;
    if (currentRecruiter) return `${baseUrl}/${locale}/recruiter/${page}`;
    return `${baseUrl}/${locale}/${page}`;
  };

  const socialLinks = [
    { icon: Facebook, href: "https://www.facebook.com/squarehubeu", label: "Facebook" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/squarehub-eu/", label: "LinkedIn" },
    { icon: Instagram, href: "https://www.instagram.com/squarehub.eu/", label: "Instagram" },
  ];

  const footerSections = [
    ...(!currentRecruiter ? [{
      titleId: "footer.sections.candidates.title",
      links: [
        { labelId: "footer.sections.candidates.jobs", href: currentCandidate ? `${baseUrl}/${locale}/candidate?tab=jobs` : `${baseUrl}/${locale}/job-offers` },
        { labelId: "footer.sections.candidates.companies", href: `${baseUrl}/${locale}/companies` },
        ...(!currentCandidate ? [{ labelId: "footer.sections.candidates.sign_up", href: `${baseUrl}/${locale}/candidates/sign_up` }] : []),
        { labelId: "footer.sections.candidates.career_advice", href: `${baseUrl}/${locale}/candidates/posts` },
      ],
    }] : []),
    ...(!currentCandidate && !currentRecruiter ? [{
      titleId: "footer.sections.recruiters.title",
      links: [
        { labelId: "footer.sections.recruiters.employer_space", href: `${baseUrl}/${locale}/recruiters` },
        { labelId: "footer.sections.recruiters.post_job", href: `${baseUrl}/${locale}/recruiter/job_offer_creation` },
        { labelId: "footer.sections.recruiters.create_page", href: `${baseUrl}/${locale}/recruiters/sign_up` },
      ],
    }] : []),
    // {
    //   titleId: "footer.sections.squarehub.title",
    //    links: [
    //      // { labelId: "footer.sections.squarehub.mission", href: `${baseUrl}/${locale}/mission` },
    //      // { labelId: "footer.sections.squarehub.team", href: `${baseUrl}/${locale}/team` },
    //      // { labelId: "footer.sections.squarehub.careers", href: `${baseUrl}/${locale}/careers` },
    //    ],
    // },
    {
      titleId: "footer.sections.help.title",
      links: [
        { labelId: "footer.sections.help.faq", href: currentCandidate ? `${baseUrl}/${locale}/candidate/FAQ` : `${baseUrl}/${locale}/faq` },
        { labelId: "footer.sections.help.contact", href: spacePath("contact-us") },
        { labelId: "footer.sections.help.report_bug", href: spacePath("report-a-bug") },
        { labelId: "footer.sections.help.terms", href: currentCandidate ? `${baseUrl}/${locale}/candidate/terms-of-use` : `${baseUrl}/${locale}/terms-of-use` },
        { labelId: "footer.sections.help.privacy", href: currentCandidate ? `${baseUrl}/${locale}/candidate/privacy-policy` : `${baseUrl}/${locale}/privacy-policy` },
      ],
    },
  ];

  useEffect(() => {
    if (setFooterRendered) {
      setFooterRendered(true);
    }
  }, [setFooterRendered]);

  return (
    <Box
      component="footer"
      id="client-sh-footer"
      sx={{ bgcolor: "hsl(178 93% 10%)", color: "hsl(0 0% 100%)" }}
    >
      {/* CTA bar — hidden when logged in */}
      {!isLoggedIn && <Box sx={{ borderBottom: "1px solid hsl(0 0% 100% / 0.1)" }}>
        <Container maxWidth="lg" sx={{ maxWidth: "72rem", py: "2.5rem", px: { xs: "1rem", sm: "1.5rem", lg: "2rem" } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: "1.25rem",
            }}
          >
            <Box>
              <Box
                component="h3"
                sx={{
                  m: 0,
                  mb: "0.25rem",
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  fontFamily: "Segoe UI Black, Work Sans, sans-serif",
                }}
              >
                <FormattedMessage id={isRecruiter ? "footer.cta.recruiter_title" : "footer.cta.title"} />
              </Box>
              <Box
                component="p"
                sx={{
                  m: 0,
                  color: "hsl(0 0% 100% / 0.5)",
                  fontSize: "0.875rem",
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                }}
              >
                <FormattedMessage id={isRecruiter ? "footer.cta.recruiter_subtitle" : "footer.cta.subtitle"} />
              </Box>
            </Box>
            <Box
              component="a"
              href={isRecruiter ? `/${locale}/recruiters/sign_up` : `/${locale}/candidates/sign_up`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                px: "1.25rem",
                py: "0.75rem",
                bgcolor: "hsl(153 68% 25%)",
                color: "hsl(0 0% 100%)",
                fontSize: "0.875rem",
                fontWeight: 700,
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                borderRadius: "0.75rem",
                textDecoration: "none",
                flexShrink: 0,
                transition: "background-color 150ms",
                "&:hover": {
                  bgcolor: "hsl(153 68% 25% / 0.9)",
                },
                "&:hover .footer-arrow": {
                  transform: "translateX(2px)",
                },
              }}
            >
              <FormattedMessage id={isRecruiter ? "footer.cta.recruiter_button" : "footer.cta.button"} />
              <ArrowRight size={14} className="footer-arrow" style={{ transition: "transform 150ms" }} />
            </Box>
          </Box>
        </Container>
      </Box>}

      {/* Main footer content */}
      <Container maxWidth="lg" sx={{ maxWidth: "72rem", py: { xs: "2.5rem", md: "3.5rem" }, px: { xs: "1rem", sm: "1.5rem", lg: "2rem" } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: isLoggedIn ? "repeat(3, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))" },
            gap: "2rem",
            ...(currentRecruiter && {
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }),
          }}
        >
          {/* Logo & Description column */}
          <Box sx={{ gridColumn: { xs: "span 2", md: "span 1" } }}>
            <Box
              component="a"
              href="/"
              sx={{ display: "inline-block", mb: "1rem" }}
            >
              <Box
                component="img"
                src={Logo}
                alt="Squarehub"
                sx={{ height: "1.5rem", filter: "brightness(0) invert(1)" }}
              />
            </Box>
            <Box
              component="p"
              sx={{
                m: 0,
                mb: "1rem",
                color: "hsl(0 0% 100% / 0.4)",
                fontSize: "0.85rem",
                lineHeight: 1.6,
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="footer.description" />
            </Box>
            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <Box
                  key={label}
                  component="a"
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: "2rem",
                    height: "2rem",
                    borderRadius: "0.5rem",
                    bgcolor: "hsl(0 0% 100% / 0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "hsl(0 0% 100%)",
                    transition: "background-color 150ms",
                    "&:hover": {
                      bgcolor: "hsl(0 0% 100% / 0.1)",
                    },
                  }}
                >
                  <Icon size={14} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Link columns */}
          {footerSections.map((section) => (
            <Box key={section.titleId}>
              <Box
                component="h4"
                sx={{
                  m: 0,
                  mb: "0.75rem",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "hsl(0 0% 100% / 0.7)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                }}
              >
                <FormattedMessage id={section.titleId} />
              </Box>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {section.links.map((link) => (
                  <Box component="li" key={link.labelId}>
                    <Box
                      component="a"
                      href={link.href}
                      sx={{
                        fontSize: "0.85rem",
                        color: "hsl(0 0% 100% / 0.4)",
                        textDecoration: "none",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                        transition: "color 150ms",
                        "&:hover": {
                          color: "hsl(0 0% 100%)",
                        },
                      }}
                    >
                      <FormattedMessage id={link.labelId} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Copyright */}
        <Box
          sx={{
            mt: "2.5rem",
            pt: "1.5rem",
            borderTop: "1px solid hsl(0 0% 100% / 0.05)",
            textAlign: "center",
          }}
        >
          <Box
            component="p"
            sx={{
              m: 0,
              fontSize: "0.75rem",
              color: "hsl(0 0% 100% / 0.25)",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            © Squarehub {new Date().getFullYear()}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
